import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const useCitySearch = (query, open) => {
  const [state, setState] = useState({ results: [], loading: false });
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/cities", { params: { q: query.trim(), limit: 50 } });
        if (live) setState({ results: data.results, loading: false });
      } catch {
        if (live) setState({ results: [], loading: false });
      }
    }, 180);
    return () => { live = false; clearTimeout(t); };
  }, [query, open]);
  return state;
};

export const CityCombobox = ({ value, onChange, error, id = "city", label = "Home city", placeholder = "Search any city worldwide", testId = "apply-city-input" }) => {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listId = useId();
  const wrap = useRef(null);
  const { results, loading } = useCitySearch(query, open);
  const typed = query.trim();
  const options = typed && !results.some((r) => r.toLowerCase() === typed.toLowerCase()) && !loading ? [...results, `__custom:${typed}`] : results;

  useEffect(() => { setQuery(value || ""); }, [value]);
  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    const close = (e) => { if (wrap.current && !wrap.current.contains(e.target)) { setOpen(false); setQuery(value || ""); } };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [value]);

  const pick = (opt) => {
    const city = opt.startsWith("__custom:") ? opt.slice(9) : opt;
    onChange(city);
    setQuery(city);
    setOpen(false);
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, options.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && open && options[active]) { e.preventDefault(); pick(options[active]); }
    else if (e.key === "Escape") { setOpen(false); setQuery(value || ""); }
  };

  return (
    <div ref={wrap} className="relative">
      <label htmlFor={id} className="field-label">{label}</label>
      <div className="relative">
        <input id={id} className="field pr-10" role="combobox" aria-expanded={open} aria-controls={listId} aria-autocomplete="list"
          aria-activedescendant={open && options[active] ? `${listId}-${active}` : undefined} aria-invalid={!!error} autoComplete="off"
          placeholder={placeholder} value={query} maxLength={80}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onClick={() => setOpen(true)} onKeyDown={onKey}
          data-testid={testId} />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          {loading && open ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </div>
      {open && (
        <ul id={listId} role="listbox" className="absolute z-30 mt-2 max-h-72 w-full overflow-y-auto rounded-lg border border-border bg-card py-1 shadow-xl" data-testid={`${testId}-listbox`}>
          {options.length === 0 && !loading && <li className="px-4 py-3 text-sm text-muted-foreground">No cities found</li>}
          {options.map((opt, i) => (
            <li key={opt} id={`${listId}-${i}`} role="option" aria-selected={i === active}
              className={cn("cursor-pointer px-4 py-2.5 text-sm", i === active ? "bg-secondary text-foreground" : "text-foreground/85")}
              onMouseEnter={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); pick(opt); }} data-testid={`${testId === "apply-city-input" ? "city" : testId}-option-${i}`}>
              {opt.startsWith("__custom:") ? <>Use “{opt.slice(9)}”</> : opt}
            </li>
          ))}
        </ul>
      )}
      {error && <p className="mt-1.5 text-sm text-destructive" role="alert">{error}</p>}
    </div>
  );
};
